import React from 'react';

import {
	Alert,
	Badge,
	Card,
	CardBody,
	CardHeader,
	Col,
	Collapse,
	FormGroup,
	Input,
	Label,
	Nav,
	NavItem,
	NavLink,
	Row,
	Table
} from 'reactstrap';
import {productService} from '../services/product.service';

class ProductSync extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			error: null,
			products: [],
			summary: {
				total: 0,
				withPipedrive: 0,
				withCin7: 0,
				fullyMatched: 0,
				archived: 0
			},
			productTypes: [],
			expandedTypes: {},
			manufacturers: [],
			selectedManufacturer: '',
			activeTab: 'retail',
			includeArchived: false
		};
	}

	componentDidMount() {
		this.loadProducts(this.state.activeTab, this.state.includeArchived);
	}

	loadProducts = (account, includeArchived) => {
		this.setState({loading: true, error: null});
		productService.getProductSyncStatus(account, includeArchived)
			.then(response => {
				if (response.Success) {
					const products = response.Data.products || [];
					const types = [...new Set(products.map(p => p.Type))].sort();
					const manufacturers = [...new Set(products.map(p => p.Manufacturer).filter(m => m))].sort();
					this.setState(prevState => ({
						loading: false,
						products: products,
						summary: response.Data.summary,
						productTypes: types,
						manufacturers: manufacturers,
						expandedTypes: {},
						// Keep the manufacturer filter when it still applies, so
						// toggling archived products does not silently reset it.
						selectedManufacturer: manufacturers.includes(prevState.selectedManufacturer)
							? prevState.selectedManufacturer
							: ''
					}));
				} else {
					this.setState({
						loading: false,
						error: response.Message || 'Failed to load data'
					});
				}
			})
			.catch(err => {
				this.setState({
					loading: false,
					error: err.message || 'Failed to load data'
				});
			});
	}

	handleTabChange = (tab) => {
		if (tab !== this.state.activeTab) {
			this.setState({activeTab: tab});
			this.loadProducts(tab, this.state.includeArchived);
		}
	}

	getProductsByType = (type) => {
		const {products, selectedManufacturer} = this.state;
		return products
			.filter(p => p.Type === type)
			.filter(p => !selectedManufacturer || p.Manufacturer === selectedManufacturer)
			.sort((a, b) => (a.Manufacturer || '').localeCompare(b.Manufacturer || ''));
	}

	formatTypeName = (type) => {
		if (!type) return 'Unknown';
		return type.charAt(0).toUpperCase() + type.slice(1);
	}

	toggleType = (type) => {
		this.setState(prevState => ({
			expandedTypes: {
				...prevState.expandedTypes,
				[type]: !prevState.expandedTypes[type]
			}
		}));
	}

	handleManufacturerChange = (e) => {
		this.setState({selectedManufacturer: e.target.value});
	}

	handleIncludeArchivedChange = (e) => {
		// setState is async, so pass the new value straight through to the reload.
		const checked = e.target.checked;
		this.setState({includeArchived: checked});
		this.loadProducts(this.state.activeTab, checked);
	}

	render() {
		const {loading, error, summary, productTypes, expandedTypes, manufacturers, selectedManufacturer, activeTab, includeArchived} = this.state;

		if (error) {
			return (
				<div className="content">
					<Alert color="danger">{error}</Alert>
				</div>
			);
		}

		return (
			<div className="content">
				{/* Account Tabs */}
				<Nav tabs style={{marginBottom: '20px'}}>
					<NavItem>
						<NavLink
							className={activeTab === 'retail' ? 'active' : ''}
							onClick={() => this.handleTabChange('retail')}
							style={{cursor: 'pointer'}}
						>
							Retail
						</NavLink>
					</NavItem>
					<NavItem>
						<NavLink
							className={activeTab === 'c&i' ? 'active' : ''}
							onClick={() => this.handleTabChange('c&i')}
							style={{cursor: 'pointer'}}
						>
							C&I
						</NavLink>
					</NavItem>
				</Nav>

				<Alert color="primary" isOpen={loading}>
					Loading product data...
				</Alert>

				{!loading && (
					<>
						{/* Summary Cards */}
						<Row>
							<Col lg="3" md="6">
								<Card className="card-stats">
									<CardBody>
										<Row>
											<Col xs="12">
												<div className="numbers">
													<p className="card-category">Total OpenSolar Products</p>
													<h3 className="card-title">{summary.total}</h3>
												</div>
											</Col>
										</Row>
									</CardBody>
								</Card>
							</Col>
							<Col lg="3" md="6">
								<Card className="card-stats">
									<CardBody>
										<Row>
											<Col xs="12">
												<div className="numbers">
													<p className="card-category">With Pipedrive Match</p>
													<h3 className="card-title">{summary.withPipedrive}</h3>
												</div>
											</Col>
										</Row>
									</CardBody>
								</Card>
							</Col>
							<Col lg="3" md="6">
								<Card className="card-stats">
									<CardBody>
										<Row>
											<Col xs="12">
												<div className="numbers">
													<p className="card-category">With Cin7 Match</p>
													<h3 className="card-title">{summary.withCin7}</h3>
												</div>
											</Col>
										</Row>
									</CardBody>
								</Card>
							</Col>
							<Col lg="3" md="6">
								<Card className="card-stats">
									<CardBody>
										<Row>
											<Col xs="12">
												<div className="numbers">
													<p className="card-category">Fully Matched</p>
													<h3 className="card-title">{summary.fullyMatched}</h3>
												</div>
											</Col>
										</Row>
									</CardBody>
								</Card>
							</Col>
							{includeArchived && (
								<Col lg="3" md="6">
									<Card className="card-stats">
										<CardBody>
											<Row>
												<Col xs="12">
													<div className="numbers">
														<p className="card-category">Archived / Removed</p>
														<h3 className="card-title">{summary.archived}</h3>
													</div>
												</Col>
											</Row>
										</CardBody>
									</Card>
								</Col>
							)}
						</Row>

						{/* Manufacturer Filter */}
						<Row>
							<Col md="4">
								<Card>
									<CardBody>
										<label>Filter by Manufacturer</label>
										<Input
											type="select"
											value={selectedManufacturer}
											onChange={this.handleManufacturerChange}
										>
											<option value="">All Manufacturers</option>
											{manufacturers.map(manufacturer => (
												<option key={manufacturer} value={manufacturer}>
													{manufacturer}
												</option>
											))}
										</Input>
									</CardBody>
								</Card>
							</Col>
							<Col md="4">
								<Card>
									<CardBody>
										<label>Archived Products</label>
										<FormGroup check style={{marginTop: '10px'}}>
											<Label check>
												<Input
													type="checkbox"
													checked={includeArchived}
													onChange={this.handleIncludeArchivedChange}
												/>
												Show products archived or removed from OpenSolar
												<span className="form-check-sign" />
											</Label>
										</FormGroup>
									</CardBody>
								</Card>
							</Col>
						</Row>

						{/* Product Tables by Type */}
						{productTypes.map(type => {
							const typeProducts = this.getProductsByType(type);
							if (typeProducts.length === 0) return null;

							return (
								<Row key={type}>
									<Col>
										<Card>
											<CardHeader
												onClick={() => this.toggleType(type)}
												style={{cursor: 'pointer'}}
											>
												<h4>
													<i className={`fa fa-chevron-${expandedTypes[type] ? 'down' : 'right'}`} style={{marginRight: '10px', width: '15px'}} />
													{this.formatTypeName(type)} ({typeProducts.length})
												</h4>
											</CardHeader>
											<Collapse isOpen={expandedTypes[type]}>
												<CardBody>
													<Table responsive>
													<thead>
														<tr>
															<th>OpenSolar Code</th>
															<th>OpenSolar Name</th>
															<th>Manufacturer</th>
															<th>Has Cost</th>
														<th>Has Labour</th>
															<th>Pipedrive Match</th>
															<th>Pipedrive Name</th>
															<th>Cin7 Match</th>
															<th>Cin7 Name</th>
														</tr>
													</thead>
													<tbody>
														{typeProducts.map(product => (
															<tr key={product.ID} className={product.IsArchived ? 'text-muted' : ''}>
																<td>
																	{product.Code}
																	{product.IsArchived && (
																		<Badge color="secondary" style={{marginLeft: '6px'}}>Archived</Badge>
																	)}
																</td>
																<td>{product.Name}</td>
																<td>{product.Manufacturer}</td>
																<td>
																	{product.HasCost ? (
																		<Badge color="success">Yes</Badge>
																	) : (
																		<Badge color="danger">No</Badge>
																	)}
																</td>
																<td>
																	{product.HasLabour ? (
																		<Badge color="success">Yes</Badge>
																	) : (
																		<Badge color="danger">No</Badge>
																	)}
																</td>
																<td>
																	{product.HasPipedriveMatch ? (
																		<Badge color="success">{product.PipedriveProductCode}</Badge>
																	) : (
																		<Badge color="danger">Missing</Badge>
																	)}
																</td>
																<td>{product.PipedriveProductName || '—'}</td>
																<td>
																	{product.HasPipedriveMatch ? (
																		product.HasCin7Match ? (
																			<Badge color="success">Matched</Badge>
																		) : (
																			<Badge color="danger">Missing</Badge>
																		)
																	) : (
																		<span>—</span>
																	)}
																</td>
																<td>{product.Cin7ProductName || '—'}</td>
															</tr>
														))}
													</tbody>
												</Table>
												</CardBody>
											</Collapse>
										</Card>
									</Col>
								</Row>
							);
						})}
					</>
				)}
			</div>
		);
	}
}

export default ProductSync;
